import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import WaitlistForm from '@/components/WaitlistForm';

interface Hotspot {
  x: number; // percent from left
  y: number; // percent from top
  title: string;
  body: string;
  below?: boolean; // open card below the dot (for dots near the top edge)
}

interface Feature {
  title: string;
  desc: string;
}

interface BookingStep {
  num: string;
  text: string;
}

const hotspots: Hotspot[] = [
  {
    x: 50, y: 8, below: true,
    title: 'The Skylight',
    body: 'A custom-built artificial skylight — tunable from warm to cool daylight, following a circadian curve through the day. Flicker-free at camera speeds, because you will photograph yourself here. Between sessions, a UV-C cycle helps sanitize the room; an occupancy sensor guarantees it never runs while anyone is inside.',
  },
  {
    x: 17, y: 76,
    title: 'The Toilet',
    body: 'A wall-hung Toto Neorest — heated seat, adjustable water pressure and temperature. A handheld shattaf (hand bidet) sits beside it, standard in every room — whether you’re performing wudu before prayer, prefer bidet hygiene, or just want a more comfortable experience.',
  },
  {
    x: 60, y: 62,
    title: 'The Counter',
    body: 'A terrazzo counter cast with crushed porcelain from broken toilets — the city’s old problem, ground down and set into the surface of its fix. It tops a refurbished vintage sideboard — a different find in every room — raised open underneath so a wheelchair rolls right up.',
  },
  {
    x: 63, y: 34,
    title: 'The Mirror',
    body: 'Vanity lighting wraps the mirror at eye level — flattering, horizontal light for touch-ups and makeovers, instead of the overhead glare that hollows your face. High color accuracy is in the spec. Skin tone is the whole point.',
  },
  {
    x: 36, y: 89,
    title: 'The Floor',
    body: 'Sixty inches of clear turning space — the full ADA circle, so a wheelchair turns all the way around without reversing into anything. Every room, every time. Nobody hunts for the accessible one.',
  },
  {
    x: 86, y: 78,
    title: 'The Bench',
    body: 'A teak-slat bench with an easy-clean cushion — for waiting, resting, nursing, or just a breath. A wall changing table folds down for little ones — and a clamp-on extension converts the bench for adult changing, in any room, on request.',
  },
];

const roomFeatures: Feature[] = [
  { title: 'ADA-Compliant Automatic Doors', desc: 'Wide, sensor-activated doors for wheelchair users, parents with strollers, and anyone who needs hands-free entry. Every room meets or exceeds ADA requirements.' },
  { title: 'Curated Music', desc: 'In-ceiling Sonos speakers start calming ambient music the moment your door closes — or take over and play your own.' },
  { title: 'Hospital-Grade Cleaning', desc: 'After every session: hospital-grade sanitization of all surfaces, fresh linens and restocked supplies, a verified cleanliness inspection, and automated UV-C disinfection before the next guest.' },
  { title: 'Timer Display', desc: 'A timer appears only when your session nears its end — a heads-up without a countdown. No anxiety about overstaying. No surprise lock-outs.' },
];

const safetyFeatures: Feature[] = [
  { title: 'Cameras outside, never inside', desc: 'External cameras monitor entry points for safety. Zero cameras inside rooms. Your privacy is absolute.' },
  { title: 'Fall detection and wellness checks', desc: 'Sensors only. For longer sessions, staff check in periodically — trained in first aid and emergency response.' },
  { title: 'QR code accountability', desc: 'Every booking is linked to a Maslow account. Maslow knows who’s in each room and when. This deters misuse while protecting your privacy.' },
  { title: 'Emergency communication', desc: 'Every room has a call button that connects directly to on-site staff or emergency services. Help is there if you need it.' },
  { title: 'Automatic unlock after session time', desc: 'If your session ends, the door unlocks automatically (you can always leave earlier). This is a safety feature — no one gets locked in.' },
];

const bookingSteps: BookingStep[] = [
  { num: '1', text: 'Open the Maslow app or visit maslow.nyc' },
  { num: '2', text: 'Find a Maslow location (map shows availability in real-time)' },
  { num: '3', text: 'Book a session' },
  { num: '4', text: 'Get your QR code (displays on your phone)' },
  { num: '5', text: 'Scan at the door (opens member hallway; proximity sensors unlock your room door automatically when you arrive)' },
  { num: '6', text: 'Use the room (do what you came to do)' },
  { num: '7', text: 'Leave when ready (door locks automatically behind you)' },
];

const TAB_NAMES = ['The Experience', 'Privacy & Safety', 'How It Works', 'For Everyone', 'Why'];

const RoomsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [spot, setSpot] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-maslow-cream">
      <Helmet>
        <title>The Rooms | Private restrooms in SoHo | Maslow NYC</title>
        <meta name="description" content="Private restrooms in SoHo, New York. A Toto Neorest with hand bidet, an artificial skylight, a terrazzo counter, a teak bench, and the full ADA turning circle in every room." />
        <link rel="canonical" href="https://maslow.nyc/rooms" />
      </Helmet>

      {/* Hero Section — compact; the folder below is the hero */}
      <section className="relative pt-10 pb-4 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-maslow-navy mb-3">
              The Rooms
            </h1>
            <p className="text-base md:text-lg text-[color-mix(in_srgb,var(--navy)_75%,transparent)] font-light max-w-2xl mx-auto">
              Private, safe, thoughtfully designed spaces for restroom access, nursing, prayer, changing—whatever you need.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabbed folder */}
      <section className="px-4 md:px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          {/* Tab rail */}
          <div className="flex flex-wrap gap-x-8 gap-y-1 px-2 mb-5" role="tablist" aria-label="The Rooms sections">
            {TAB_NAMES.map((name, i) => (
              <button
                key={name}
                role="tab"
                aria-selected={tab === i}
                onClick={() => { setTab(i); setSpot(null); }}
                className={`relative py-2 text-[11px] uppercase tracking-[0.26em] transition-opacity duration-300 ${tab === i ? 'text-maslow-navy opacity-100' : 'text-maslow-navy opacity-70 hover:opacity-100'}`}
              >
                {name}
                <span className={`absolute left-0 right-0 bottom-0 h-px bg-maslow-gold origin-left transition-transform duration-300 ${tab === i ? 'scale-x-100' : 'scale-x-0'}`} />
              </button>
            ))}
          </div>

          {/* Navy panel */}
          <div className="bg-maslow-navy text-maslow-cream rounded-sm border border-[rgba(212,175,106,0.25)] p-6 md:p-10 shadow-[0_24px_60px_-34px_rgba(28,43,58,0.65)]">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              {/* TAB 0 — THE EXPERIENCE */}
              {tab === 0 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-2">Step inside. Touch the room.</h3>
                  <p className="text-white/80 max-w-2xl mb-6">
                    Every Maslow room is built to one standard: a room you would actually choose. Explore it — each point of light is a decision.
                  </p>

                  {/* Hotspot stage */}
                  <div className="relative max-w-3xl mx-auto" onMouseLeave={() => setSpot(null)}>
                    <img
                      src="/room-skylight-wide.webp"
                      alt="Maslow room — walnut-grid artificial skylight, backlit mirror, terrazzo counter on a red lacquered vintage sideboard, teak bench, wall-hung toilet with hand bidet"
                      width={1800}
                      height={1200}
                      decoding="async"
                      className="w-full h-auto block rounded-sm"
                    />
                    {hotspots.map((h, i) => (
                      <button
                        key={h.title}
                        aria-label={h.title}
                        onMouseEnter={() => setSpot(i)}
                        onClick={(e) => { e.stopPropagation(); setSpot(spot === i ? null : i); }}
                        className="absolute w-9 h-9 -translate-x-1/2 -translate-y-1/2 z-10"
                        style={{ left: `${h.x}%`, top: `${h.y}%` }}
                      >
                        <span className="absolute inset-0 m-auto w-3.5 h-3.5 rounded-full bg-maslow-gold/70 animate-ping" />
                        <span className="absolute inset-0 m-auto w-3.5 h-3.5 rounded-full bg-maslow-gold shadow-[0_0_0_2px_rgba(255,255,255,0.85)]" />
                      </button>
                    ))}
                    {hotspots.map((h, i) => (
                      <div
                        key={`card-${h.title}`}
                        className={`absolute z-20 w-72 md:w-80 max-w-[86vw] bg-maslow-cream border border-[rgba(212,175,106,0.55)] rounded-sm p-5 shadow-xl transition-opacity duration-300 ${spot === i ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                        style={{
                          left: `${Math.min(Math.max(h.x, 20), 72)}%`,
                          top: `${h.y}%`,
                          transform: h.below ? 'translate(-50%, 26px)' : 'translate(-50%, calc(-100% - 26px))',
                        }}
                      >
                        <h4 className="font-serif text-xl font-semibold text-maslow-navy mb-1.5">{h.title}</h4>
                        <p className="text-[13.5px] leading-relaxed text-[rgba(28,43,58,0.88)]">{h.body}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-center text-xs uppercase tracking-[0.18em] text-white/50 mt-3 mb-10">Hover or tap a point</p>

                  {/* Beyond the render */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {roomFeatures.map((f) => (
                      <div key={f.title} className="bg-white/5 border border-[rgba(212,175,106,0.18)] rounded-sm p-5">
                        <h4 className="text-maslow-gold font-medium mb-1.5 text-[15px]">{f.title}</h4>
                        <p className="text-white/80 text-[13.5px] leading-relaxed">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 1 — PRIVACY & SAFETY */}
              {tab === 1 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-10">Built for safety</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {safetyFeatures.map((f) => (
                      <div key={f.title} className="bg-white/5 border border-[rgba(212,175,106,0.18)] rounded-sm p-5">
                        <h4 className="text-maslow-gold font-medium mb-1.5 text-[15px]">{f.title}</h4>
                        <p className="text-white/80 text-[13.5px] leading-relaxed">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 2 — HOW IT WORKS */}
              {tab === 2 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-4">How it works</h3>
                  <p className="text-white/70 mb-10">When the doors open, here’s how it works:</p>
                  <div className="space-y-6 max-w-2xl">
                    {bookingSteps.map((s) => (
                      <div key={s.num} className="flex items-start gap-5">
                        <div className="w-10 h-10 bg-maslow-gold text-maslow-navy rounded-full flex items-center justify-center shrink-0 font-medium">
                          {s.num}
                        </div>
                        <p className="text-white/85 pt-2">{s.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TAB 3 — FOR EVERYONE */}
              {tab === 3 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-6">You know your needs better than I do.</h3>
                  <div className="max-w-2xl space-y-5 text-white/85 leading-relaxed">
                    <p>
                      Maslow is designed with the people who’ll use it. One standard for every room — accessibility isn’t a special version. Manual door controls and staff assistance for Sabbath observers.
                    </p>
                    <p>
                      Every room starts with the essentials, and the rest is set up for your session: nursing and pumping (a sterilized breast pump, pumping bags, wipes, and hands-free accessories, ready when you walk in), prayer (a prayer mat with directional indicators for qibla, and a dedicated foot-washing station in the Hull for wudu), baby care (diapers in multiple sizes, wipes, changing pad, disposal bags), or just the restroom — nothing extra, a spotless private space when you need it quickly.
                    </p>
                    <p>
                      And the needs I haven’t heard of yet — that’s what this is for:{' '}
                      <a href="mailto:hello@maslow.nyc?subject=Accessibility%20Feedback" className="text-maslow-gold hover:opacity-80 transition-opacity">
                        tell me what I got wrong.
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {/* TAB 4 — WHY */}
              {tab === 4 && (
                <div>
                  <h3 className="text-3xl md:text-4xl font-serif text-maslow-cream mb-6">Why I built it this way</h3>
                  <div className="max-w-2xl space-y-5 text-white/85 leading-relaxed">
                    <p>
                      Most public restrooms are designed to deter use. Uncomfortable seats. Harsh lighting. No privacy. Minimal amenities. The message is clear: get in, get out, don’t stay.
                    </p>
                    <p>
                      I designed the rooms on the opposite idea: <strong className="text-maslow-cream">stay as long as you need. Be comfortable. Feel safe.</strong>
                    </p>
                    <p>
                      A good restroom, a quiet place to nurse, a clean place to pray — these are ordinary needs. I build for them the way a good hotel would: as a matter of course.
                    </p>
                  </div>
                  <p className="font-serif text-2xl text-maslow-cream border-l-[3px] border-maslow-gold pl-6 my-8 max-w-2xl">
                    I call them restrooms because that’s what they are. The room is what needed fixing. A restroom should be a moment of rest, and this city doesn’t offer many.
                  </p>
                  <p className="text-white/70">
                    I researched. I planned. I prototyped. I won’t know if I got it right until you use it. If something doesn’t work, tell me:{' '}
                    <a href="mailto:hello@maslow.nyc" className="text-maslow-gold hover:text-white transition-colors">hello@maslow.nyc</a>
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Be First Section */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-sm font-bold uppercase tracking-widest text-maslow-gold-ink mb-4">
              Be First
            </h2>
            <div className="w-16 h-0.5 bg-maslow-gold-ink mb-8"></div>
            <h3 className="text-3xl md:text-4xl font-serif text-maslow-navy mb-6">
              Be there when the doors open
            </h3>
            <p className="text-lg text-[color-mix(in_srgb,var(--navy)_70%,transparent)] leading-relaxed mb-8">
              Joining the waitlist is free. It is how you will hear first.
            </p>
            <WaitlistForm source="rooms" />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RoomsPage;
